import { Link } from 'react-router-dom';

import { DOC_PAGES } from '../../docs';
import type { FeatureState } from '../../docs/types';

import { CheckCell, StateBadge } from './Blocks';

/**
 * The developer audit checklist, generated from the documentation itself.
 *
 * Every row comes from a page's own `status`, so this table and the feature
 * pages cannot contradict each other — there is one source and two renderings
 * of it. A feature that nobody has documented does not appear here at all,
 * which is itself the signal that it needs a page.
 */

const STATE_ORDER: Record<FeatureState, number> = { missing: 0, partial: 1, shipped: 2 };

export function AuditChecklist() {
  // Unfinished things first. A checklist that buries its gaps under the
  // finished work is decoration rather than a tool.
  const rows = DOC_PAGES.filter((page) => page.status !== undefined).sort((a, b) => {
    const byState = STATE_ORDER[a.status!.state] - STATE_ORDER[b.status!.state];
    return byState !== 0 ? byState : a.title.localeCompare(b.title);
  });

  const counts = {
    shipped: rows.filter((r) => r.status!.state === 'shipped').length,
    partial: rows.filter((r) => r.status!.state === 'partial').length,
    missing: rows.filter((r) => r.status!.state === 'missing').length,
  };

  const gaps = rows.flatMap((page) =>
    (page.status!.gaps ?? []).map((gap) => ({ page, gap })),
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Tally label="Shipped" value={counts.shipped} tone="text-accent" />
        <Tally label="Partial" value={counts.partial} tone="text-amber-300" />
        <Tally label="Not usable" value={counts.missing} tone="text-red-300" />
      </div>

      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[46rem] text-left text-sm">
          <thead className="bg-panel-raised">
            <tr>
              <th className="px-3 py-2 font-medium text-neutral-400">Feature</th>
              <th className="px-3 py-2 font-medium text-neutral-400">State</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Front</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Back</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Data</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Auth</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Perms</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Errors</th>
              <th className="px-3 py-2 font-medium text-neutral-400">Tests</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((page) => (
              <tr key={page.slug} className="border-t border-line">
                <td className="px-3 py-2">
                  <Link to={`/docs/${page.slug}`} className="text-neutral-200 hover:text-accent">
                    {page.title}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <StateBadge state={page.status!.state} />
                </td>
                <td className="px-3 py-2"><CheckCell value={page.status!.frontend} /></td>
                <td className="px-3 py-2"><CheckCell value={page.status!.backend} /></td>
                <td className="px-3 py-2"><CheckCell value={page.status!.persistence} /></td>
                <td className="px-3 py-2"><CheckCell value={page.status!.auth} /></td>
                <td className="px-3 py-2"><CheckCell value={page.status!.permissions} /></td>
                <td className="px-3 py-2"><CheckCell value={page.status!.errorHandling} /></td>
                <td className="px-3 py-2"><CheckCell value={page.status!.tests} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section>
        <h2 className="text-base font-semibold text-neutral-100">
          Everything currently unfinished ({gaps.length})
        </h2>
        <p className="mt-1 text-sm text-neutral-500">
          Collected from every feature page. This is the backlog the documentation itself is
          asserting.
        </p>
        <ul className="mt-3 space-y-2">
          {gaps.map(({ page, gap }, i) => (
            <li key={i} className="rounded-lg border border-line bg-panel-raised p-3">
              <Link
                to={`/docs/${page.slug}`}
                className="text-xs uppercase tracking-wide text-neutral-500 hover:text-accent"
              >
                {page.title}
              </Link>
              <p className="mt-1 text-sm text-neutral-300">{gap}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Tally({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg border border-line bg-panel-raised px-4 py-2">
      <p className={`text-xl font-semibold ${tone}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</p>
    </div>
  );
}
